import React, { useState, useEffect } from 'react';
import { Upload, Table, Tag, Button, Space, Spin } from 'antd';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Upload as UploadIcon,
  Download,
  Trash2,
  FileText,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usersService } from '@/services/users.service';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

interface StoredFile {
  uid: string;
  id?: number;
  name: string;
  size: number;
  type?: string;
  uploadedAt: string;
  previewUrl: string;
  fileUrl?: string;
}

interface UpdateFileStorageSectionProps {
  /**
   * userId của nhân sự cần hiển thị file storage.
   * - Nếu truyền vào: ưu tiên dùng userId này (dùng cho màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (dùng cho trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
}

export const UpdateFileStorageSection: React.FC<UpdateFileStorageSectionProps> = ({ userId }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const handleRemove = async (uid: string) => {
    const target = files.find((file) => file.uid === uid);
    if (!target) return;

    // Nếu file có id từ API, gọi API để xóa
    if (target.id) {
      try {
        // TODO: Thêm method deletePersonalFile vào usersService khi backend sẵn sàng
        // await usersService.deletePersonalFile(target.id);
        // eslint-disable-next-line no-console
        console.log('Delete file with id:', target.id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting file:', error);
        return;
      }
    }

    // Xóa file khỏi state
    setFiles((prev) => {
      const fileToRemove = prev.find((file) => file.uid === uid);
      // Nếu là object URL (file mới upload chưa lưu), revoke nó
      if (fileToRemove?.previewUrl && fileToRemove.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter((file) => file.uid !== uid);
    });
  };

  // Fetch danh sách file từ API
  useEffect(() => {
    const fetchFiles = async () => {
      // Ưu tiên userId truyền từ props (màn quản lý nhân sự),
      // nếu không có thì fallback sang user hiện tại (trang hồ sơ cá nhân).
      let targetUserId: number | null = null;

      if (typeof userId === 'number') {
        targetUserId = userId;
      } else if (user?.id) {
        targetUserId = parseInt(user.id.toString(), 10);
      }

      if (!targetUserId || Number.isNaN(targetUserId)) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const personalFiles = await usersService.getPersonalFiles(targetUserId);

        // Map dữ liệu từ API sang StoredFile interface
        const mappedFiles: StoredFile[] = (personalFiles || []).map((file) => ({
          uid: `file-${file.id}`,
          id: file.id,
          name: file.fileName || 'Không có tên',
          size: file.fileSize || 0,
          type: file.fileType || file.fileExtension || undefined,
          uploadedAt: file.uploadedAt
            ? new Date(file.uploadedAt).toLocaleString('vi-VN')
            : new Date().toLocaleString('vi-VN'),
          previewUrl: file.fileUrl || '',
          fileUrl: file.fileUrl,
        }));

        setFiles(mappedFiles);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching personal files in UpdateFileStorageSection:', error);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [userId, user?.id]);

  const handleDownload = (file: StoredFile) => {
    // Ưu tiên dùng fileUrl từ API, nếu không có thì dùng previewUrl
    const url = file.fileUrl || file.previewUrl;
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizes.length - 1,
    );
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(1)} ${sizes[i]}`;
  };

  const uploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => {
      const now = new Date();
      const previewUrl = URL.createObjectURL(file as File);
      setFiles((prev) => [
        {
          uid: file.uid,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: now.toLocaleString(),
          previewUrl,
        },
        ...prev,
      ]);

      // Ngăn Ant Design tự upload – chỉ xử lý phía client, backend tích hợp sau
      return false;
    },
  };

  const columns: ColumnsType<StoredFile> = [
    {
      title: 'Tên file',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => {
            const url = record.fileUrl || record.previewUrl;
            if (url) {
              window.open(url, '_blank');
            }
          }}
        >
          <Space size={8}>
            <FileText size={16} />
            <span>{text}</span>
          </Space>
        </Button>
      ),
    },
    {
      title: 'Dung lượng',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size: number) => <span>{formatSize(size)}</span>,
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type?: string) =>
        type ? <Tag color="blue">{type.split('/').pop()}</Tag> : <Tag>Khác</Tag>,
    },
    {
      title: 'Thời gian tải lên',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 200,
      render: (value: string) => <span>{value}</span>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<Download size={16} />}
            onClick={() => handleDownload(record)}
          >
            Tải xuống
          </Button>
          <Button
            type="link"
            danger
            icon={<Trash2 size={16} />}
            onClick={() => handleRemove(record.uid)}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.fileStorageSection}>
      <div className={styles.fileStorageUploadWrapper}>
        <Upload.Dragger
          {...uploadProps}
          className={styles.fileStorageDragger}
        >
          <div className={styles.fileStorageDraggerContent}>
            <UploadIcon size={40} className={styles.fileStorageUploadIcon} />
            <p className={styles.fileStorageDraggerTitle}>
              Kéo thả file vào đây để tải lên hoặc
            </p>
            <Button type="primary" size="middle">
              Chọn tệp
            </Button>
            <p className={styles.fileStorageDraggerHint}>
              Hỗ trợ các định dạng PDF, Word, Excel, hình ảnh... Dung lượng tối
              đa 20MB/tệp.
            </p>
          </div>
        </Upload.Dragger>
      </div>

      <div className={styles.fileStorageTableWrapper}>
        <div className={styles.fileStorageTableHeader}>
          <span className={styles.fileStorageTableTitle}>Danh sách file</span>
          <span className={styles.fileStorageTableCount}>
            Tổng số: {files.length} file
          </span>
        </div>

        <Spin spinning={loading}>
          <Table
            rowKey="uid"
            columns={columns}
            dataSource={files}
            pagination={{ pageSize: 5, showSizeChanger: false }}
            locale={{
              emptyText: loading ? 'Đang tải...' : 'Chưa có file nào được tải lên.',
            }}
          />
        </Spin>
      </div>
    </div>
  );
};

